import React, { ComponentType, ReactElement } from "react";
import { CircleAlertIcon, CircleCheckIcon, ClockIcon, PillBottleIcon } from "../../../../../../components/Icons";
import { EPrescriptionStatus } from "../../../../../../services/prescriptions/prescriptions.interface";
import StatusChip from "../../../components/StatusChip/StatusChip";
import { useTheme } from "@mui/material";

interface IPrescriptionStatusProps {
  status: EPrescriptionStatus;
}

interface IStatusTheme {
  badgeColor: string;
  color: string;
}

interface IIconProps {
  width?: number;
  height?: number;
  color?: string;
}

const getStatusTheme = (theme: any): Record<EPrescriptionStatus, IStatusTheme> => ({
  [EPrescriptionStatus.ACTIVE]: { badgeColor: theme.palette.green.light, color: theme.palette.green.main },
  [EPrescriptionStatus.EXPIRING_SOON]: { badgeColor: theme.palette.yellow.light, color: theme.palette.yellow.main },
  [EPrescriptionStatus.EXPIRED]: { badgeColor: theme.palette.red.lighter, color: theme.palette.red.dark },
  [EPrescriptionStatus.NO_REFILLS_REMAINING]: { badgeColor: theme.palette.red.lighter, color: theme.palette.red.dark },
  [EPrescriptionStatus.DECLINED]: { badgeColor: theme.palette.neutral.light, color: theme.palette.black.main },
});

const DISPLAY_NAMES: Record<EPrescriptionStatus, string> = {
  [EPrescriptionStatus.ACTIVE]: "Active",
  [EPrescriptionStatus.EXPIRING_SOON]: "Expiring Soon",
  [EPrescriptionStatus.EXPIRED]: "Expired",
  [EPrescriptionStatus.NO_REFILLS_REMAINING]: "No Refills Remaining",
  [EPrescriptionStatus.DECLINED]: "Declined",
};

const STATUS_ICONS: Record<EPrescriptionStatus, ComponentType<IIconProps>> = {
  [EPrescriptionStatus.ACTIVE]: CircleCheckIcon,
  [EPrescriptionStatus.EXPIRING_SOON]: ClockIcon,
  [EPrescriptionStatus.EXPIRED]: CircleAlertIcon,
  [EPrescriptionStatus.NO_REFILLS_REMAINING]: PillBottleIcon,
  [EPrescriptionStatus.DECLINED]: CircleAlertIcon,
};

const getStatusIcon = (status: EPrescriptionStatus, color: string): ReactElement => {
  const IconComponent = STATUS_ICONS[status] || CircleAlertIcon;
  return <IconComponent width={12} height={12} color={color} />;
};

const PrescriptionStatus = ({ status }: IPrescriptionStatusProps) => {
  const theme = useTheme();
  const STATUS_THEME = getStatusTheme(theme);

  const { badgeColor, color } = STATUS_THEME[status] || {
    badgeColor: theme.palette.neutral.light,
    color: theme.palette.black.main,
  };
  const displayName = DISPLAY_NAMES[status] || status;
  const icon = getStatusIcon(status, color);

  return <StatusChip label={displayName} badgeColor={badgeColor} color={color} icon={icon} />;
};

export default PrescriptionStatus;
